import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useAppDispatch, useAppSelector } from 'hooks';

import { PAGE_SIZE_SM } from '~constants/common';

import { useSearch } from '~libs/searchParams';

import { ClickableIcon, Toolbar } from '~components';
import { SearchBar } from '~components/form-fields/search';
import { faMagnifyingGlass, faUser } from '~components/icon';
import { Row, TableView } from '~components/table-view';

import columnsFactory from '~support-at-home/forms/budgets/clientsWithBudgetsList';
import searchFields from '~support-at-home/forms/budgets/clientsWithBudgetsSearch';

import { getAll as getClientsWithBudgets } from '~support-at-home/actions/clientsWithBudgets';

import { clear } from '~support-at-home/reducers/clientsWithBudgets';

import {
  selectClientsWithBudgets,
  selectClientsWithBudgetsLoading,
  selectClientsWithBudgetsTotal,
} from '~support-at-home/selectors/clientsWithBudgets';

import ClientsWithBudgetsModal from './ClientsWithBudgetsModal';

const ClientsWithBudgets: React.FC = () => {
  const dispatch = useAppDispatch();

  const clientsWithBudgets = useAppSelector(selectClientsWithBudgets);
  const loading = useAppSelector(selectClientsWithBudgetsLoading);
  const clientsWithBudgetsTotal = useAppSelector(selectClientsWithBudgetsTotal);

  const [selectedClientId, setSelectedClientId] = useState<number | null | undefined>(null);
  const [isOpen, setIsOpen] = useState(false);

  const isLoading = loading === 'pending';

  const data: Row[] = useMemo(
    () => (Array.isArray(clientsWithBudgets) ? (clientsWithBudgets as Row[]) : []),
    [clientsWithBudgets],
  );

  const [searchBarOpen, setSearchBarOpen] = useState<boolean>(false);
  const [searchParams, setSearchParams] = useSearch('clients-with-budgets-search', {});

  const [params, setParams] = useState({ limit: PAGE_SIZE_SM });

  useEffect(() => {
    load();
  }, [searchParams]);

  const load = (loadParams: { [key: string]: any } = {}) => {
    const newParams = {
      ...params,
      ...loadParams,
    };

    dispatch(getClientsWithBudgets({ ...newParams, ...searchParams }));
    setParams(newParams);
  };

  const search = (values: { [key: string]: any }) => {
    setSearchParams(values);
  };

  useEffect(() => {
    load();
    return () => {
      dispatch(clear([]));
    };
  }, []);

  const toggleClient = useCallback(
    (toggle: boolean, clientId?: number | null | undefined) => {
      if (toggle && clientId) {
        const client = clientsWithBudgets.find((c) => c.id === clientId);

        // Only open the modal if the client has at least one budget
        if (client && client?.budgets?.length > 0) {
          setSelectedClientId(clientId);
          setIsOpen(toggle);
        }
      } else {
        setSelectedClientId(clientId);
        setIsOpen(toggle);
      }
    },
    [clientsWithBudgets],
  );

  const columns = useMemo(() => columnsFactory((id) => toggleClient(true, id)), [toggleClient]);

  return (
    <div className="p-2">
      <Toolbar
        id="clients-with-budgets"
        rightCluster={
          <ClickableIcon
            className="me-2"
            icon={faMagnifyingGlass}
            id="clients-with-budgets-search-button"
            onClick={() => setSearchBarOpen(!searchBarOpen)}
            toolTip="Toggle Search Bar"
          />
        }
      />
      <SearchBar isOpen={searchBarOpen} fields={searchFields} initialValues={searchParams} onSubmit={search} />
      <TableView
        id="clients-with-budgets"
        data={data}
        columns={columns}
        isLoading={isLoading}
        emptyIcon={faUser}
        allowPagination={true}
        onLoadMore={() => load({ limit: params.limit + PAGE_SIZE_SM })}
        showCount={true}
        totalCount={clientsWithBudgetsTotal}
        emptyMessage="No clients with budgets found"
        readonly
        striped
      />
      <ClientsWithBudgetsModal id={selectedClientId} isOpen={isOpen} setIsOpen={setIsOpen} />
    </div>
  );
};

export default ClientsWithBudgets;
